/**
 * DynamicLonePair - Motion-enabled lone pair component
 *
 * Subscribes to atom MotionValues and updates lone pair position in real-time.
 * Lone pairs follow their atoms instantly (no spring animation).
 * Dynamically repositions to avoid bond intersections during dragging.
 */

import { memo, useMemo, useEffect } from 'react';
import { motion, useTransform } from 'motion/react';
import { useMotion } from '../../contexts/MotionContext';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { useSelection } from '../../selection';
import { useFeedbackWithContext } from '../../feedback';
import type { ElectronData } from '../../rendering/useSVGGeneration';
import type { ChargeData } from '../../rendering/useSVGGeneration';

export interface DynamicLonePairProps {
  electronData: ElectronData;
  allElectronData?: ElectronData[]; // All electron data for this atom
  allChargeData?: ChargeData[]; // All charge data to avoid charge positions
  onClick?: (e: React.MouseEvent<SVGElement>, lonePairId: string) => void;
}

/**
 * Renders a lone pair or radical that follows its atom using MotionValues
 */
export const DynamicLonePair = memo(function DynamicLonePair({ electronData, allElectronData, onClick }: DynamicLonePairProps) {
  const { getAtomMotion } = useMotion();
  const format = useFormatOverride();

  // Generate unique ID for this lone pair/radical
  // Find the index of this electron among all electrons for this atom
  const electronIndex = allElectronData
    ? allElectronData.filter(e => e.atomId === electronData.atomId).indexOf(electronData)
    : 0;
  const lonePairId = `${electronData.atomId}-lp${electronIndex}`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(lonePairId);
  const atomMotion = getAtomMotion(electronData.atomId);

  if (!atomMotion) {
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(lonePairId) ?? false;
  const isHovered = selection?.isHovered(lonePairId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  const radius = format.lonePair.radius;
  const gap = format.lonePair.ratios.gap;

  // Use static lone pair direction from initial layout
  // This prevents flickering during atom dragging by maintaining stable positions
  // The layout2d package has already calculated optimal positions that avoid bonds
  const staticDir = useMemo(() => {
    return [electronData.dir[0], electronData.dir[1]];
  }, [electronData.dir]);

  // Calculate center position of the lone pair / radical
  // IMPORTANT: All hooks must be called unconditionally at top level
  // Motion v12: use function form with .get() for automatic dependency tracking
  const centerX = useTransform(() => atomMotion.x.get() + staticDir[0]);
  const centerY = useTransform(() => atomMotion.y.get() + staticDir[1]);

  // Calculate perpendicular vector from static direction for proper orientation
  const perpData = useMemo(() => {
    const d = staticDir;
    const dirLength = Math.sqrt(d[0] * d[0] + d[1] * d[1]);
    if (dirLength === 0) return [0, 1]; // Fallback
    const normalizedDir = [d[0] / dirLength, d[1] / dirLength];
    return [-normalizedDir[1], normalizedDir[0]]; // Perpendicular in 2D
  }, [staticDir]);


  // Calculate rotation angle from perpendicular direction for ellipse orientation
  // The ellipse should be rotated so its major axis aligns with the perpendicular vector
  const rotationAngle = useMemo(() => {
    const perp = perpData;
    // Calculate angle in degrees (atan2 returns radians)
    return Math.atan2(perp[1], perp[0]) * 180 / Math.PI;
  }, [perpData]);

  // Click handler
  const handleClick = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e, lonePairId);
    }
  };

  if (electronData.type === 'radical') {
    // Single dot for radical with hit box for arrow drawing
    return (
      <g id={lonePairId} className="svg-radical">
        {/* Hit box - 1.5x visual size for easy targeting using FeedbackManager styling */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={feedback.currentStyle.hitboxRadius ?? radius * 1.5}
          fill={feedback.currentStyle.lonePairColor ?? '#90bcd0'}
          fillOpacity={feedback.currentStyle.fillOpacity ?? 0}
          stroke={feedback.currentStyle.hitboxStroke}
          strokeWidth={feedback.currentStyle.lonePairStrokeWidth ?? 0}
          style={{ pointerEvents: 'auto', cursor: feedback.currentStyle.cursor ?? 'crosshair' }}
          onClick={handleClick}
          onMouseEnter={() => selection?.setHovered(lonePairId)}
          onMouseLeave={() => selection?.setHovered(null)}
        />
      </g>
    );
  } else {
    return (
      <g id={lonePairId} className="svg-lonepair">
        {/* Hit box - ellipse covering both dots for easy targeting using FeedbackManager styling */}
        <motion.ellipse
          cx={centerX}
          cy={centerY}
          rx={feedback.currentStyle.hitboxRx ?? (radius * 2.5 + gap / 2)}
          ry={feedback.currentStyle.hitboxRy ?? (radius * 2.8)}
          fillOpacity={feedback.currentStyle.fillOpacity ?? 0}
          fill={feedback.currentStyle.lonePairColor ?? '#90bcd0'}
          stroke={feedback.currentStyle.hitboxStroke}
          strokeWidth={feedback.currentStyle.lonePairStrokeWidth ?? 0}
          style={{
            pointerEvents: 'auto',
            cursor: feedback.currentStyle.cursor ?? 'crosshair',
            transformOrigin: 'center',
            rotate: `${rotationAngle}deg`
          }}
          onClick={handleClick}
          onMouseEnter={() => selection?.setHovered(lonePairId)}
          onMouseLeave={() => selection?.setHovered(null)}
        />
      </g>
    );
  }
});
